import {useState, useEffect} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {Navigate, useParams, useNavigate} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import type {TimelineMatrix} from "../../../sdo/TimelineMatrix.ts";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const HOUR_LABELS: string[] = [];
for (let h = 0; h < 24; h++) {
    if (h === 0) HOUR_LABELS.push("12am");
    else if (h < 12) HOUR_LABELS.push(`${h}am`);
    else if (h === 12) HOUR_LABELS.push("12pm");
    else HOUR_LABELS.push(`${h - 12}pm`);
}

function daysInMonthCount(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

// TODO: Replace with API call to TimelineManager.getMatrix(year, month)
function buildTestData(year: number, month: number): TimelineMatrix {
    const dim = daysInMonthCount(year, month);
    const rows: TimelineMatrix["rows"] = {};
    for (let day = 1; day <= dim; day++) {
        const counts: Record<number, number> = {};
        let total = 0;
        for (let h = 0; h < 24; h++) {
            const c = Math.random() < 0.15 ? Math.floor(Math.random() * 8) + 1 : 0;
            counts[h] = c;
            total += c;
        }
        if (total > 0) {
            rows[day] = {total, counts};
        }
    }
    return {rows};
}

// --- Cell styles mirroring desktop card-timeline-* CSS ---

const headerCellSx = {
    fontSize: "0.6rem",
    fontWeight: 700,
    color: "#02cfee",
    textAlign: "center" as const,
    padding: "4px 2px",
    backgroundColor: "rgba(0, 50, 100, 0.4)",
    borderRadius: "6px",
    userSelect: "none" as const,
};

const labelCellSx = {
    fontSize: "0.65rem",
    fontWeight: 700,
    color: "#F2F5FA",
    textAlign: "center" as const,
    padding: "4px 2px",
    cursor: "pointer",
    borderRadius: "10px",
    "&:hover": {
        color: "#02cfee",
    },
};

const countCellSx = {
    fontSize: "0.6rem",
    fontWeight: 600,
    color: "#38ff82",
    textAlign: "center" as const,
    padding: "4px 2px",
    backgroundColor: "#091920",
    borderRadius: "10px",
    opacity: 0.7,
    cursor: "pointer",
    "&:hover": {
        opacity: 1.0,
    },
};

const zeroCellSx = {
    fontSize: "0.6rem",
    fontWeight: 600,
    color: "#3F464F",
    textAlign: "center" as const,
    padding: "4px 2px",
    backgroundColor: "#091920",
    borderRadius: "10px",
    opacity: 0.7,
    cursor: "default",
};

function UXTimelineMonth() {
    const {networkUID, year: yearParam, month: monthParam} = useParams();
    const navigate = useNavigate();
    const year = Number(yearParam);
    const month = Number(monthParam);
    const monthName = MONTH_SHORT[month - 1] ?? "";
    const dim = daysInMonthCount(year, month);

    const [matrix, setMatrix] = useState<TimelineMatrix | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // TODO: Replace with API call
        const data = buildTestData(year, month);
        setMatrix(data);
        setLoading(false);
    }, [networkUID, year, month]);

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title={`Timeline - ${monthName} ${year}`}
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Timeline", linkTo: `/networks/${networkUID}/timeline`, activeView: false},
                            {display: String(year), linkTo: `/networks/${networkUID}/timeline/${year}`, activeView: false},
                            {display: monthName, linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%" height="100%" alignItems="start">
                        {loading ? (
                            <Typography sx={{color: "#38ff82", fontSize: "0.85rem", padding: "16px"}}>Loading...</Typography>
                        ) : (
                            <Box sx={{width: "100%", overflowX: "auto", padding: "8px"}}>
                                <Box sx={{display: "grid", gridTemplateColumns: "40px repeat(24, 1fr)", gap: "2px", minWidth: "1000px"}}>
                                    {/* Header row */}
                                    <Typography sx={headerCellSx}>Day</Typography>
                                    {HOUR_LABELS.map(label => (
                                        <Typography key={label} sx={headerCellSx}>{label}</Typography>
                                    ))}

                                    {/* Day rows */}
                                    {Array.from({length: dim}, (_, i) => i + 1).map(day => {
                                        const row = matrix?.rows[day];

                                        return [
                                            <Typography
                                                key={`d-${day}`}
                                                sx={labelCellSx}
                                                onClick={() => navigate(`/networks/${networkUID}/timeline/${year}/${month}/${day}`)}
                                            >
                                                {day}
                                            </Typography>,
                                            ...HOUR_LABELS.map((_, hi) => {
                                                const count = row?.counts[hi] ?? 0;
                                                return (
                                                    <Typography
                                                        key={`h-${day}-${hi}`}
                                                        sx={count > 0 ? countCellSx : zeroCellSx}
                                                        onClick={count > 0 ? () => navigate(`/networks/${networkUID}/timeline/${year}/${month}/${day}?hour=${hi}`) : undefined}
                                                    >
                                                        {count}
                                                    </Typography>
                                                );
                                            }),
                                        ];
                                    })}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXTimelineMonth;
